// Refund.js
// ** React Imports
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Date Picker Imports
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";


// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import CustomLabel from "components/CustomLabel";
import { getServiceRequests } from "screens/DispatchBoard/logic/DispatchBoardLogic";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { getInvoices, SavePayment, getInvoicePayments } from "../logic/PaymentLogic";
import { toast } from "react-toastify";
import { useServiceRequestPermission } from "screens/Requests/permissions/RequestPermissions";
import { getUser } from 'utils/auth';

// ** Dialog Imports
import PaymentReceiptDialog from "./PaymentReceipt";

// Utility: date formatting helper
import dayjs from "dayjs";
const prettyDate = (d) => (d ? dayjs(d).format("MMMM D, YYYY") : "");

export default function Refund({ invoiceID,RefundProps, handleClosePaymentDetail, fetchPayments }) {
  const {
    openRefundDlg,
    handleCloseRefundDlg,
    refundData,
    openPaymentReceipt,
    handleOpenPaymentReceipt,    // will call AFTER save
    handleClosePaymentReceipt,
  } = RefundProps;

  const formatPrice = (val) => {
    if (val == null || val === "") return "N/A";
      const num = typeof val === "number" ? val : Number(val);
      return isNaN(num)
        ? "N/A"
        : `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  //#region Permissions
  const { ServiceRequestPermissionProps } = useServiceRequestPermission();
  const { hasServiceGetPermission } = ServiceRequestPermissionProps; 
  //#endregion Permissions

  // console.log(refundData)
  const navigate = useNavigate();
  const theme = useTheme();

  // fetched data
  const [customerData, setCustomerData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // form state
  const [amount, setAmount] = useState("");
  const [refundDate, setRefundDate] = useState(dayjs());
  const [paidBy, setPaidBy] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedRefundPayment, setSavedRefundPayment] = useState(null);
  const [paymentsHistory, setPaymentsHistory] = useState([]);
  const [remainingRefundableAmount, setRemainingRefundableAmount] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [errors, setErrors] = useState({});

  const User = getUser();

  // console.log(User)
  // determine sections
  const isInvoice =
    refundData?.paymentType === "Invoice" ||
    refundData?.entity === "Invoice";
  const isAdvance =
    refundData?.paymentType === "Deposit" ||
    refundData?.entity === "Advance";

    const fetchPaymentHistoryAndCalculateRemaining = async () => {
      if (!refundData?.requestID && !refundData?.invoiceID) return;
      
      setLoadingPayments(true);
      try {
        // You'll need to implement getPaymentHistory in your PaymentLogic
        // This should fetch all payments for this request/invoice
        const payments = await getInvoicePayments(
          refundData.invoiceID, navigate
        );
        
        setPaymentsHistory(payments || []);
        
        // Calculate remaining refundable amount
        
        const originalPaymentAmount = payments
          .filter(payment => 
            payment.paymentStatus === "Paid" 
            // && payment.paymentID === refundData.paymentID
          )
          .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
        
        // Sum all existing refunds for this payment
        const totalRefundedSoFar = payments
          .filter(payment => 
            payment.paymentStatus === "Refunded" 
            // && payment.paymentID === refundData.paymentID
          )
          .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);

        // console.log(payments)
        // console.log(refundData)
        // console.log(originalPaymentAmount)
        // console.log(totalRefundedSoFar)
        const remaining = originalPaymentAmount - totalRefundedSoFar;
        setRemainingRefundableAmount(Math.max(0, remaining));
        
      } catch (error) {
        console.error("Error fetching payment history:", error);
        setRemainingRefundableAmount(Number(refundData.amount) || 0); // Fallback to original amount
      } finally {
        setLoadingPayments(false);
      }
    };

  // load initial refundData into form
  useEffect(() => {
    if (!refundData) return;
    
    fetchPaymentHistoryAndCalculateRemaining();
    setAmount(
        // if user is editing an existing refund use that,
        // otherwise default to the original payment amount
      (refundData.refundAmount ?? refundData.amount ?? "")
      .toString()
    );
    setRefundDate(refundData.refundDate ? dayjs(refundData.refundDate) : dayjs());
    setPaidBy(refundData.refundedBy ?? refundData.paidBy ?? "");
  }, [refundData]);

  // fetch customer
  useEffect(() => {
    if (!refundData?.requestID) return;

    if(hasServiceGetPermission){
      setLoadingCustomer(true);
      getServiceRequests()
        .then((reqs) => {
          const req = refundData.requestType === "WorkOrder"
            ? reqs.find(r =>
                r.requestID === refundData.requestID &&
                r.entityType === "WorkOrder")
            : reqs.find(r =>
                r.requestID === refundData.requestID &&
                r.entityType === "Assessment");
          if (req?.customerID) return GetCustomer(req.customerID);
        })
        .then((res) => setCustomerData(res?.[0] ?? null))
        .catch(() => setCustomerData(null))
        .finally(() => setLoadingCustomer(false));
    }
  }, [refundData?.requestID, refundData?.requestType, hasServiceGetPermission]);

  // fetch invoice
  useEffect(() => {
    if (!isInvoice || !refundData?.invoiceID) return;
    setLoadingInvoice(true);
    getInvoices()
      .then((all) =>
        setInvoiceData(all.find(i => i.invoiceID === refundData.invoiceID) || null)
      )
      .catch(() => setInvoiceData(null))
      .finally(() => setLoadingInvoice(false));
  }, [refundData?.invoiceID, isInvoice]);

  if (!refundData) return null;

  // formatting helper
  const formatAmount = (value) => {
    // Allow empty value
    if (value === "") return "";
    
    // Remove any non-digit characters except decimal point
    let cleaned = value.replace(/[^0-9.]/g, "");
    
    // Allow standalone dot for typing values like .50
    if (cleaned === ".") return ".";
    
    // Handle multiple decimal points
    const decimalPoints = cleaned.split('.').length - 1;
    if (decimalPoints > 1) {
      cleaned = cleaned.substring(0, cleaned.lastIndexOf('.'));
    }
    
    // Split into integer and decimal parts
    const [intPart, decPart = ""] = cleaned.split(".");
    
    // Format integer part with commas (only if not empty)
    const formattedInt = intPart ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
    
    // Return formatted amount
    if (decPart !== undefined && decPart !== null) {
      // If there's a decimal part, include the dot
      return `${formattedInt}.${decPart.slice(0, 2)}`;
    }
    return formattedInt;
  };

  // save & preview
  const handlePreview = async () => {
    // simple validation
    setErrors({});
    const newErrors = {};

      // simple validation
    if (!amount) newErrors.amount = "Refund amount is required";
    if (!refundDate) newErrors.refundDate = "Refund date is required";
    if (!paidBy) newErrors.paidBy = "Refunded by is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

  const numAmt = Number(amount.replace(/,/g, ""));

  if (numAmt <= 0) {
    setErrors({ amount: "Refund amount must be greater than 0" });
    return;
  }
  
  if (numAmt > remainingRefundableAmount) {
    setErrors({ 
      amount: `Refund amount cannot exceed remaining refundable amount of ${formatPrice(remainingRefundableAmount)}` 
    });
    return;
  }

    const payload = {
      paymentID: 0,
      paymentType: isInvoice ? "Invoice" : "Deposit",
      invoiceID: refundData.invoiceID ?? 0,
      requestID: refundData.requestID ?? 0,
      amount: Number(amount.replace(/,/g, "")),
      paymentDate: refundDate.format("YYYY-MM-DD"),
      paymentMethod: refundData.paymentMethod,  // reuse original
      paymentStatus: "Refunded",
      paidBy: User && User.fullName || "",
      notes: refundData.notes || refundData.note,
    };

    setSaving(true);
    try {
      const result = await SavePayment(payload, navigate);
      // console.log(result)
      if (result && !result.isError) {
        // new approach: keep the actual saved payment
        const saved = {
          ...RefundProps.refundData,
          amount: payload.amount,
          paymentDate: payload.paymentDate,
          paidBy: payload.paidBy,
          status: "Refunded"
        };
        setSavedRefundPayment(saved);
        handleCloseRefundDlg();
        handleOpenPaymentReceipt();        
      } else {        
        console.log("Failed to save refund.");
      }
    } catch {
      console.log("Error saving refund.");
    } finally {
      setSaving(false);
    }
  };
const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined) return '';

  const str = value.toString();

  // Allow partial numbers while typing (e.g. ".", "0.", ".5")
  if (str === '' || str === '.' || str === '0.' || /^\.\d*$/.test(str)) {
    return str;
  }

  // Remove commas and format integer part
  const parts = str.replace(/,/g, '').split('.');
  if (parts[0] !== '') {
    const num = Number(parts[0]);
    // Check if it's actually a valid number before formatting
    if (!isNaN(num)) {
      parts[0] = num.toLocaleString('en-US');
    }
  }

  return parts.join('.');
};

const removeCommas = (value) => value.replace(/,/g, '');
  
  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        open={openRefundDlg}
        onClose={handleCloseRefundDlg}
        aria-labelledby="refund-dialog-title"
        sx={{ "& .MuiDialog-paper": { width: "790px", maxWidth: "95%" } }}
      >
        <DialogTitle
          sx={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Refund Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              REF-{refundData.paymentID}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={5}>

            {/* Invoice / Advance sections */}
            {isInvoice && (
              <>
                <Grid item xs={12}><Typography variant="h4">Invoice Information</Typography></Grid>
                <Grid item xs={12}><Divider sx={{ color: "text.disabled" }} /></Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary">Invoice ID</Typography>
                  <Typography variant="h6">
                    {invoiceData
                      ? `INV-${invoiceData.invoiceID}`
                      : refundData.invoiceID
                        ? `INV-${refundData.invoiceID}`
                        : ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">Invoice Amount</Typography>
                  <Typography variant="h6">
                  {loadingInvoice
                    ? "Loading..."
                    : formatPrice(invoiceData?.totalAmount ?? refundData.amount)}
                  </Typography>
                </Grid>
                {/* ... invoice date, due date, remaining */}
              </>
            )}

            {isAdvance && (
              <>
                <Grid item xs={12}><Typography variant="h4">Advance Information</Typography></Grid>
                <Grid item xs={12}><Divider sx={{ color: "text.disabled" }} /></Grid>

                <Grid item xs={6}>
                  <Typography color="text.secondary">Type</Typography>
                  <Typography variant="h6">
                    {refundData.requestType === "Assessment"
                      ? `Assessment (AST-${refundData.requestID})`
                      : `Work Order (WO-${refundData.requestID})`}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">Paid Amount</Typography>
                  <Typography variant="h6">
                   {formatPrice(refundData.amount)}
                  </Typography>
                </Grid>
              </>
            )}

            {/* Customer */}
            <Grid item xs={12} mt={2}><Typography variant="h4">Customer Information</Typography></Grid>
            <Grid item xs={12}><Divider sx={{ color: "text.disabled" }} /></Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Customer Name</Typography>
              <Typography variant="h6">
                {loadingCustomer
                  ? "Loading..."
                  : customerData
                    ? (
                      customerData.customerType === "Individual"
                        ? `${customerData.firstName} ${customerData.lastName}`
                        : customerData.companyName
                    )
                    : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Email</Typography>
              <Typography variant="h6"sx={{ wordBreak: 'break-word' }}>
                {customerData?.email || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Phone</Typography>
              <Typography variant="h6">
                {customerData?.phoneNumbers?.[0]?.phoneNo || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Note</Typography>
              <Typography variant="h6">
                {refundData.notes || refundData.note || "N/A"}
              </Typography>
            </Grid>

            {/* Original Payment */}
            <Grid item xs={12} mt={2}><Typography variant="h4">Payment Information</Typography></Grid>
            <Grid item xs={12}><Divider sx={{ color: "text.disabled" }} /></Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Paid Amount</Typography>
              <Typography variant="h6">{formatPrice(refundData.amount)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Payment Date</Typography>
              <Typography variant="h6">
                {refundData.paymentDate ? prettyDate(refundData.paymentDate) : "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Payment Method</Typography>
              <Typography variant="h6">{refundData.paymentMethod ? refundData.paymentMethod : "N/A"}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography color="text.secondary">Paid By</Typography>
              <Typography variant="h6">{refundData.paidBy ? refundData.paidBy : "N/A"}</Typography>
            </Grid>

            {/* Refund Form */}
            <Grid item xs={12} mt={2}><Typography variant="h4">Refund Information</Typography></Grid>
            <Grid item xs={12}><Divider sx={{ color: "text.disabled" }} /></Grid>

            <Grid item xs={6}>
              <CustomTextField
                fullWidth
                label="Refunded Amount"
                placeholder="Enter amount"
                value={formatNumberWithCommas(amount)}
                onChange={e => {
                  setAmount(formatAmount(removeCommas(e.target.value)));
                  // Clear error when user starts typing
                  if (errors.amount) {
                    setErrors(prev => ({ ...prev, amount: undefined }));
                  }
                }}
                error={Boolean(errors.amount)}
                helperText={errors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomLabel label="Refund Date" />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: "100%" }}
                  value={refundDate}
                  onChange={(newValue) => {
                    setRefundDate(newValue);
                    // Clear error when user selects a date
                    if (errors.refundDate) {
                      setErrors(prev => ({ ...prev, refundDate: undefined }));
                    }
                  }}
                  slotProps={{ textField: { size: "small",
                    error: Boolean(errors.refundDate),
                    helperText: errors.refundDate
                   } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <CustomTextField
                fullWidth
                label="Refunded By"
                placeholder="Enter name"
                value={User && User.fullName}
                // onChange={e => setPaidBy(e.target.value)}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            padding: "16px 24px !important",
            boxShadow: `0px -4px 8px rgb(0,0,0,0.1)`,
            zIndex: 1
          }}
        >
          <Button
            variant="text"
            color={theme.palette.mode === 'dark' ? 'secondary' : "primary"}
            onClick={handleCloseRefundDlg}
            disabled={saving}
            fullWidth={{ xs: true, sm: false }}
            sx={{ order: { xs: 1, sm: 0 } }}
          >
            Back
          </Button>
          
          <Button
            variant="text"
            color={theme.palette.mode === 'dark' ? 'secondary' : "primary"}
            onClick={handlePreview}
            disabled={saving}
            startIcon={saving && <CircularProgress size={16} />}
            fullWidth={{ xs: true, sm: false }}
            sx={{ order: { xs: 2, sm: 0 } }}
          >
            Save Receipt
          </Button>
          
          <Button
            variant="contained"
            color="main"
            onClick={handlePreview}
            disabled={saving}
            fullWidth={{ xs: true, sm: false }}
            sx={{ order: { xs: 3, sm: 0 } }}
          >
            Send Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* <PaymentReceiptDialog
        open={openPaymentReceipt}
        handleClose={handleClosePaymentReceipt}
        handleClosePaymentDetail={handleClosePaymentDetail}
        handleCloseRefundDlg={handleCloseRefundDlg}
        entity="refund"
      /> */}
      <PaymentReceiptDialog
        open={openPaymentReceipt}
        handleClose={handleClosePaymentReceipt}
        handleClosePaymentDetail={handleClosePaymentDetail}
        handleCloseRefundDlg={handleCloseRefundDlg}
        entity="refund"
        payment={savedRefundPayment}       // ← your saved refund object
        customerData={customerData}            // ← fetched in RefundDialog
        invoiceData={invoiceData}              // ← fetched in RefundDialog
        fetchPayments={fetchPayments}
        invoiceID={invoiceID}
      />
    </>
  );
}
